import {use, useCallback, useState} from 'react'
import {ChevronRightIcon} from '@primer/octicons-react'
import {ActionList, CounterLabel} from '@primer/react'
import {queryForRepos} from '@github-ui/one-copilot-composer/task-chat-input-toolbar/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import {ReposSelector, isAllRepositoriesSelection, isNoRepoSelection} from '@github-ui/repos-selector'
import {ALL_REPOSITORIES_FILTER, NO_REPO_FILTER} from '../../hooks/use-repo-tasks-filter'

interface RepoFilterPickerProps {
  /** Current filter: undefined = all sessions, a special filter value, or a NWO string. */
  repoFilter: string | undefined
  /** Callback when filter changes */
  onFilterChange: (repo: AgentDropdownRepository | undefined) => void
  /** When true, render as a nested ActionList.Item (e.g. inside an overflow "..." menu). */
  nested?: boolean
  sidebarSubmenu?: boolean
  includeRepositoryPresenceOptions?: boolean
  /** Optional class applied to the picker's trigger button (e.g. to match filter button styling) */
  className?: string
}

/**
 * Repository filter picker for the agents page.
 * Wraps ReposSelector with session- and repository-scope options.
 */
export function RepoFilterPicker({
  repoFilter,
  onFilterChange,
  className,
  nested,
  sidebarSubmenu = false,
  includeRepositoryPresenceOptions = true,
}: RepoFilterPickerProps) {
  const [lastSelectedRepo, setLastSelectedRepo] = useState<AgentDropdownRepository>()
  const [hasOpened, setHasOpened] = useState(false)
  const actionListContext = use(ActionList.ContainerContext)
  const [owner, name] = repoFilter?.split('/', 2) ?? []
  const selectedRepo =
    repoFilter && owner && name && repoFilter !== NO_REPO_FILTER && repoFilter !== ALL_REPOSITORIES_FILTER
      ? lastSelectedRepo?.nameWithOwner.toLowerCase() === repoFilter.toLowerCase()
        ? lastSelectedRepo
        : {
            name: repoFilter,
            nameOnly: name,
            nameWithOwner: repoFilter,
            owner,
            enabled: true,
            defaultBranch: 'main',
            private: false,
            isEmpty: false,
          }
      : undefined
  const isNoRepo = repoFilter === NO_REPO_FILTER
  const isAllRepositories = repoFilter === ALL_REPOSITORIES_FILTER

  const repositoryLoader = useCallback(
    (query: string, defaultOwner?: string) => (hasOpened ? queryForRepos(query, defaultOwner) : Promise.resolve([])),
    [hasOpened],
  )

  const handleSelect = useCallback(
    (repo: AgentDropdownRepository | undefined) => {
      if (repo && !isNoRepoSelection(repo) && !isAllRepositoriesSelection(repo)) {
        setLastSelectedRepo(repo)
      } else {
        setLastSelectedRepo(undefined)
      }
      onFilterChange(repo)
    },
    [onFilterChange],
  )

  const picker = (
    <ReposSelector<AgentDropdownRepository>
      repositoryLoader={repositoryLoader}
      onOpen={() => setHasOpened(true)}
      selectionVariant="single"
      selectAllOption
      selectAllOptionText="All sessions"
      selectAllOptionSelected={!repoFilter}
      allRepositoriesOption={includeRepositoryPresenceOptions}
      allRepositoriesOptionSelected={isAllRepositories}
      noRepoOption={includeRepositoryPresenceOptions}
      noRepoOptionSelected={isNoRepo}
      onSelect={handleSelect}
      currentSelection={isNoRepo || isAllRepositories ? undefined : selectedRepo}
      width="medium"
      height="large"
      renderAnchor={
        nested
          ? ({anchorProps, buttonText, ariaLabel}) => (
              <ActionList.Item
                {...anchorProps}
                role="menuitem"
                aria-haspopup="dialog"
                aria-labelledby={undefined}
                aria-label={sidebarSubmenu ? undefined : ariaLabel}
                // Keep the parent overflow menu open so this anchor stays mounted and the picker
                // overlay positions against it (mimics a submenu). preventDefault skips ActionList's
                // afterSelect, which would otherwise close the menu and detach the overlay.
                onSelect={event => event.preventDefault()}
              >
                {buttonText}
                <ActionList.TrailingVisual>
                  {sidebarSubmenu && repoFilter && <CounterLabel>1</CounterLabel>}
                  <ChevronRightIcon aria-hidden="true" />
                </ActionList.TrailingVisual>
              </ActionList.Item>
            )
          : undefined
      }
      // Static label matching the sibling "Status"/"Type" filters — the active
      // selection is conveyed via aria-label (and the filtered list), not the
      // button text, so the trigger stays compact and consistent.
      buttonText="Repository"
      additionalButtonProps={{
        variant: 'invisible',
        className,
        'aria-label': `Filter by repository: ${
          isNoRepo
            ? 'No repository'
            : isAllRepositories
              ? 'All repositories'
              : selectedRepo?.nameWithOwner || 'All sessions'
        }`,
        // No leading repo icon on the trigger: siblings are text + caret only.
        leadingVisual: undefined,
      }}
    />
  )

  // ActionMenu supplies aria-checked to descendants, but listbox options need aria-selected.
  return sidebarSubmenu ? (
    <ActionList.ContainerContext value={{...actionListContext, selectionAttribute: 'aria-selected'}}>
      {picker}
    </ActionList.ContainerContext>
  ) : (
    picker
  )
}
